import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import MarkHandledAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/PreparationHeader/MarkHandledAction';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { css } from '@emotion/react';
import { Check } from 'lucide-react';
import * as React from 'react';
import { ProgressBar } from '@verclam/ui-kit';

const headerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const titleStyle = css`
	${theme.typography.heroTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const subtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.90625rem;
	line-height: 1.6;
	color: ${theme.colors.inkSecondary};
`;

const bannerStyle = (surface: string, ink: string) => css`
	display: flex;
	gap: 0.875rem;
	align-items: center;
	padding: 1rem 1.125rem;
	border-radius: ${theme.radii.large};
	background-color: ${surface};
	color: ${ink};
	font-family: ${theme.fonts.body};
`;

const checkStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: 2rem;
	height: 2rem;
	border-radius: 50%;
	background-color: ${theme.colors.validated};
	color: ${theme.colors.surface};
`;

const bannerTextStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
	flex: 1;
	min-width: 0;
	font-size: 0.78125rem;
	line-height: 1.5;
`;

const bannerTitleStyle = css`
	font-size: 0.875rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.4;
`;

const progressStyle = css`
	width: 100%;
	max-width: 20rem;
	padding-top: 0.375rem;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunView;
};

/** Title block of the preparation: opening date, label, one-line subtitle and the generating / to-review / completed banners (mutually exclusive), plus the "mark as handled" gesture when the server offers it. */
const PreparationHeader: React.FC<Props> = ({ subject, preparation }) => {
	const subtitle = React.useMemo(() => {
		if (preparation.isCompleted) {
			return 'Toutes les étapes sont vérifiées.';
		}

		if (preparation.isOpen) {
			return 'Rassemblez les éléments au fil des visites, puis générez les documents quand vous avez assez d’éléments.';
		}

		return 'La génération est lancée : la préparation ne reçoit plus d’éléments.';
	}, [preparation.isCompleted, preparation.isOpen]);

	return (
		<React.Fragment>
			<div css={headerStyle}>
				<Eyebrow>
					Préparation · ouverte le {formatLongDate(preparation.openedAt.toISOString())}
				</Eyebrow>
				<h1 css={titleStyle}>{preparation.label}</h1>
				<p css={subtitleStyle}>{subtitle}</p>
			</div>
			<ConditionalLoader data={preparation.isCompleted}>
				<div css={bannerStyle(theme.states.success.surface, theme.states.success.ink)} role="status">
					<div css={checkStyle}>
						<Check size={16} aria-hidden="true" />
					</div>
					<div css={bannerTextStyle}>
						<span css={bannerTitleStyle}>
							{preparation.completedAt !== null
								? `Préparation terminée le ${formatLongDate(preparation.completedAt.toISOString())}`
								: 'Préparation terminée'}
						</span>
						<span>
							Ses documents sont rangés dans le dossier, prêts à télécharger. Elle est désormais
							en lecture seule.
						</span>
					</div>
				</div>
			</ConditionalLoader>
			<ConditionalLoader data={preparation.isGenerating}>
				<div css={bannerStyle(theme.colors.accentSurface, theme.colors.accentDark)} role="status">
					<div css={bannerTextStyle}>
						<span css={bannerTitleStyle}>Génération en cours</span>
						<span>
							Les documents se génèrent étape après étape. Cet écran se met à jour tout seul.
						</span>
						<div css={progressStyle}>
							<ProgressBar mode="indeterminate" aria-label="Génération en cours" />
						</div>
					</div>
				</div>
			</ConditionalLoader>
			<ConditionalLoader
				data={
					!preparation.isGenerating && preparation.hasDocumentsToReview && !preparation.isCompleted
				}
			>
				<div css={bannerStyle(theme.states.warning.surface, theme.states.warning.ink)} role="status">
					<div css={bannerTextStyle}>
						<span css={bannerTitleStyle}>Documents à relire</span>
						<span>La génération est terminée. Relisez chaque document puis validez-le.</span>
					</div>
				</div>
			</ConditionalLoader>
			<ConditionalLoader data={preparation.canMarkHandled}>
				<MarkHandledAction subject={subject} preparationId={preparation.id} />
			</ConditionalLoader>
		</React.Fragment>
	);
};

export default PreparationHeader;
